import { useCallback, useLayoutEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Preserves the `#main` scroll position across URL search-param updates.
 *
 * Returns a `capture()` function — call it synchronously before any
 * `navigate({ search })` that should not jump the user back to the top
 * of the scrollable area.
 *
 * The Insights page header is `position: sticky; top: 0` inside the
 * `#main` scroll container, so any control inside it (e.g. the date
 * filter toggle) sits at the top of the un-transformed document. When
 * such a control is clicked, the browser's focus-into-view heuristic
 * uses that un-transformed position and pulls `#main` back to the top,
 * even though the control was already visible via the sticky offset.
 * Capturing the scroll position before the navigate and restoring it in
 * a `useLayoutEffect` keyed on `location.search` undoes that jump
 * before the browser paints. CORE-5601.
 */
export function usePreserveMainScroll(): () => void {
  const location = useLocation()
  const scrollPositionRef = useRef<number | null>(null)

  useLayoutEffect(() => {
    if (scrollPositionRef.current === null) return
    const main = document.getElementById('main')
    if (main) {
      main.scrollTop = scrollPositionRef.current
    }
    scrollPositionRef.current = null
  }, [location.search])

  return useCallback((): void => {
    const main = document.getElementById('main')
    if (main) {
      scrollPositionRef.current = main.scrollTop
    }
  }, [])
}
